import { ChangeDetectionStrategy, Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DiagnosisCodesRepositoryService, PatientDiagnosisCode, ReportControl } from '@xmedical/core';
import { ReportControlEditorService } from '../../services/report-control-editor.service';

/**
 * DiagnosisListRendererComponent
 *
 * Renders the patient's ICD-10 diagnosis code list in a report.
 * Uses a refreshTrigger input signal instead of PatientEventsService.
 * The consuming app should increment refreshTrigger when diagnosis codes change.
 *
 * This is the patient's own diagnosis code list, not the codes captured against a billing entry —
 * those are a separate control (BILLING_ICD10_LIST) fed from the billing entry rather than the patient.
 */
@Component({
  selector: 'xmed-diagnosis-list-renderer',
  imports: [
    MatListModule,
    MatFormFieldModule
  ],
  templateUrl: './diagnosis-list-renderer.component.html',
  styleUrl: './diagnosis-list-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiagnosisListRendererComponent implements OnInit {
  private diagnosisCodesRepository = inject(DiagnosisCodesRepositoryService);
  private editorService = inject(ReportControlEditorService);

  patientId = input.required<number>();
  control = input.required<ReportControl>();
  refreshTrigger = input<number>(0);

  diagnosisList = signal<PatientDiagnosisCode[]>([]);

  // Effect to reload when refreshTrigger changes
  private refreshEffect = effect(() => {
    const trigger = this.refreshTrigger();
    const pid = this.patientId();
    if (trigger > 0 && pid) {
      this.loadDiagnosisCodes();
    }
  });

  ngOnInit(): void {
    this.diagnosisList.set(this.control().diagnosisListValue || []);
    this.loadDiagnosisCodes();
  }

  /**
   * "U07.1 - COVID-19", or just "U07.1" when the control is set not to print descriptions, or when the
   * code has none recorded.
   *
   * The backend resolves the description from master data when the code is captured, so almost every
   * row has one. It is null only where the ICD-10 code has no match in MasterDiagnosisCodes.
   */
  describe(item: PatientDiagnosisCode): string {
    return this.control().displayDiagnosisDescription && item.description
      ? `${item.icD10Code} - ${item.description}`
      : item.icD10Code;
  }

  private loadDiagnosisCodes(): void {
    this.diagnosisCodesRepository.loadPatientDiagnosisCodes(this.patientId()).subscribe(codes => {
      const sorted = codes.sort((a, b) => a.createdDate > b.createdDate ? 1 : -1);
      this.diagnosisList.set(sorted);
      this.control().diagnosisListValue = sorted;
      this.editorService.triggerEvent();
    });
  }
}
